import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Building2, CheckCircle, Filter, Globe, Layers, Monitor, Shield, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/SEOHead";
import LanguageSelector from "@/components/LanguageSelector";

export default function SoftwareAppaltiPubbliciEuropei() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Software per la Ricerca di Bandi e Appalti Europei"
        description="Software professionale per la ricerca e il monitoraggio di bandi e appalti europei. Cerca, filtra e traccia le gare TED con TED Monitor."
        path="/it/software-appalti-pubblici-europei"
        alternates={[
          { lang: "it", href: "https://tedmonitor.eu/it/software-appalti-pubblici-europei" },
          { lang: "en", href: "https://tedmonitor.eu/european-public-procurement-software" },
          { lang: "x-default", href: "https://tedmonitor.eu/european-public-procurement-software" },
        ]}
      />

      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-mark.png" alt="TED Monitor" className="h-8 w-8" />
            <span className="font-semibold text-lg text-primary">TED Monitor</span>
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSelector />
            <Button asChild>
              <Link to="/auth">Inizia ora</Link>
            </Button>
          </div>
        </div>
      </header>

      <main>
        {/* Hero Section */}
        <section className="py-8 md:py-12 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <h1 className="text-3xl md:text-4xl font-bold text-primary mb-4 leading-tight">
              Software per la Ricerca di Bandi e Appalti Europei
            </h1>

            <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-4">
              Il nostro software specializzato nel monitoraggio e ricerca di appalti europei trasforma la complessità
              del database TED in un vantaggio competitivo per la tua azienda. Grazie all'integrazione di algoritmi di
              intelligenza artificiale, TED Monitor permette di filtrare migliaia di bandi europei in tempo reale,
              selezionando solo le opportunità pertinenti al tuo core business. Non limitarti a una ricerca manuale:
              automatizza il tuo flusso di lavoro con alert personalizzati e un'analisi predittiva dei risultati. Che
              tu stia cercando appalti pubblici europei nel settore dei servizi, delle forniture o dei lavori, la
              nostra piattaforma ti garantisce l'accesso a dati strutturati, traduzioni multilingue e scadenze sempre
              aggiornate, rendendo il monitoraggio un processo semplice, veloce e privo di errori.
            </p>

            <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-3">
              Il mercato degli appalti pubblici europei è vasto, complesso e altamente competitivo. Operare
              efficacemente richiede un software dedicato che centralizzi i dati sui bandi, semplifichi la ricerca e
              supporti decisioni più rapide.
            </p>

            <p className="text-base font-medium text-foreground">
              TED Monitor è costruito specificamente per monitorare i bandi pubblici UE pubblicati su TED.
            </p>

            {/* Internal links block (SEO + crawling) */}
            <div className="mt-6 flex flex-wrap gap-2">
              <Link
                to="/it/monitoraggio-bandi-ue"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Monitoraggio bandi UE
              </Link>
              <Link
                to="/it/alert-bandi-ted"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Alert bandi TED
              </Link>
              <Link
                to="/it/accesso-multilingue"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Accesso multilingue (24 lingue UE)
              </Link>
              <Link
                to="/it/conformita-sicurezza"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Conformità e sicurezza
              </Link>
              <Link
                to="/it/prezzi"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Prezzi
              </Link>
            </div>
          </div>
        </section>

        {/* Cos'è un software per appalti pubblici europei */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Monitor className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Cos'è un software per appalti pubblici europei
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              Un software per appalti pubblici europei è uno strumento digitale che ti aiuta a gestire un workflow
              strutturato per identificare e tracciare i bandi pubblici UE. In termini pratici, ti permette di:
            </p>

            <ul className="space-y-1.5 text-muted-foreground text-sm mb-4">
              {[
                "Accedere ai bandi UE da un'unica piattaforma centralizzata",
                "Analizzare opportunità con dati strutturati",
                "Monitorare nuove pubblicazioni e aggiornamenti nel tempo",
                "Supportare decisioni di gara con criteri coerenti",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <p className="text-muted-foreground leading-relaxed text-sm">
              A differenza dei portali istituzionali, un software dedicato migliora l'efficienza operativa e supporta la
              visibilità strategica sul mercato.
            </p>
          </div>
        </section>

        {/* Dati ufficiali da TED */}
        <section className="py-6 md:py-8 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Shield className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">Dati ufficiali UE da TED</h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              TED Monitor utilizza dati ufficiali sui bandi da{" "}
              <a
                href="https://ted.europa.eu"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Tenders Electronic Daily (TED)
              </a>
              , la fonte di riferimento UE per gli avvisi di appalto sopra soglia.
            </p>

            <p className="text-muted-foreground mb-3 text-sm">Questo garantisce:</p>
            <ul className="space-y-1.5 text-muted-foreground text-sm">
              {[
                "Copertura a livello UE in tutti gli Stati membri",
                "Informazioni verificabili e autoritative sugli appalti",
                "Coerenza con procedure e formati UE",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <p className="mt-4">
              <Link to="/data-source" className="text-primary hover:underline font-medium text-sm">
                Scopri di più sulla fonte dati →
              </Link>
            </p>
          </div>
        </section>

        {/* Funzionalità core */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Filter className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Funzionalità core per il monitoraggio appalti UE
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              Un software efficace per il procurement richiede funzionalità avanzate per mantenere il monitoraggio
              preciso e scalabile.
            </p>

            <p className="text-muted-foreground mb-3 text-sm">Con TED Monitor puoi:</p>
            <ul className="space-y-1.5 text-muted-foreground text-sm mb-4">
              {[
                "Cercare bandi per codice CPV e parole chiave",
                "Filtrare per paese e tipo di procedura",
                "Tracciare nuove pubblicazioni e aggiornamenti",
                "Lavorare con dati strutturati e comparabili",
                "Gestire alti volumi senza perdere focus",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <p className="text-muted-foreground leading-relaxed text-sm mb-4">
              Tutto in un'unica piattaforma con workflow progettato per utenti procurement.
            </p>

            <div className="flex flex-wrap gap-3">
              <Link to="/it/monitoraggio-bandi-ue" className="text-primary hover:underline font-medium text-sm">
                Esplora il monitoraggio bandi UE →
              </Link>
              <Link to="/it/alert-bandi-ted" className="text-primary hover:underline font-medium text-sm">
                Configura alert automatici →
              </Link>
            </div>
          </div>
        </section>

        {/* Complessità */}
        <section className="py-6 md:py-8 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Layers className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Progettato per la complessità del mercato europeo
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              Gli appalti pubblici europei comportano complessità che uno strumento generico tipicamente non riesce a
              gestire bene:
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
              {["Diversi framework nazionali", "Più lingue", "Formati eterogenei", "Soglie e procedure UE"].map(
                (item) => (
                  <div key={item} className="p-2 bg-background rounded-lg border text-center">
                    <p className="font-medium text-foreground text-xs">{item}</p>
                  </div>
                ),
              )}
            </div>

            <p className="text-muted-foreground leading-relaxed text-sm">
              TED Monitor è progettato per astrarre questa complessità e fornire una visione uniforme e comparabile del
              mercato dei bandi UE.
            </p>
          </div>
        </section>

        {/* Chi usa */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Users className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Chi usa un software per appalti pubblici europei
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              I software per il monitoraggio procurement sono utilizzati da organizzazioni che trattano i bandi UE come
              un canale di crescita stabile.
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
              <div className="p-2 bg-muted/50 rounded-lg border text-center">
                <Building2 className="h-5 w-5 text-primary mx-auto mb-1" />
                <p className="font-medium text-foreground text-xs">Aziende operative in più paesi UE</p>
              </div>
              <div className="p-2 bg-muted/50 rounded-lg border text-center">
                <Users className="h-5 w-5 text-primary mx-auto mb-1" />
                <p className="font-medium text-foreground text-xs">Consulenti specializzati</p>
              </div>
              <div className="p-2 bg-muted/50 rounded-lg border text-center">
                <Shield className="h-5 w-5 text-primary mx-auto mb-1" />
                <p className="font-medium text-foreground text-xs">Studi professionali</p>
              </div>
              <div className="p-2 bg-muted/50 rounded-lg border text-center">
                <Globe className="h-5 w-5 text-primary mx-auto mb-1" />
                <p className="font-medium text-foreground text-xs">PMI in espansione nel mercato UE</p>
              </div>
            </div>

            <p className="text-muted-foreground leading-relaxed text-sm">
              Una piattaforma procurement diventa strategica quando la discovery delle gare è sistematica anziché
              occasionale.
            </p>
          </div>
        </section>

        {/* Perché scegliere */}
        <section className="py-6 md:py-8 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">Perché scegliere TED Monitor</h2>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              TED Monitor si distingue perché è dedicato ai workflow di procurement UE:
            </p>

            <ul className="space-y-1.5 text-muted-foreground text-sm mb-4">
              {[
                "Utilizza solo fonti ufficiali UE sui bandi",
                "Focalizzato esclusivamente sul mercato europeo",
                "Fornisce dati strutturati e filtrabili",
                "Supporta decisioni più rapide e informate",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <p className="text-muted-foreground leading-relaxed text-sm">
              Scopri i fattori di fiducia della piattaforma:
            </p>

            <div className="mt-3 flex flex-wrap gap-3 text-sm">
              <Link to="/it/accesso-multilingue" className="text-primary hover:underline font-medium">
                Accesso multilingue →
              </Link>
              <Link to="/it/conformita-sicurezza" className="text-primary hover:underline font-medium">
                Conformità e sicurezza →
              </Link>
              <Link to="/it/monitoraggio-bandi-ue" className="text-primary hover:underline font-medium">
                Monitoraggio bandi UE →
              </Link>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-8 md:py-12 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h2 className="text-xl md:text-2xl font-semibold mb-4">
              Inizia a usare il software per appalti pubblici europei
            </h2>

            <p className="text-base mb-6 opacity-90">
              Scegli una piattaforma dedicata per monitorare i bandi UE da dati TED ufficiali—strutturati, ricercabili e
              progettati per il lavoro di procurement.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="default" variant="secondary" asChild>
                <Link to="/it/prezzi" className="gap-2">
                  Vedi i prezzi
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>

              <Button
                size="default"
                variant="outline"
                className="bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                asChild
              >
                <Link to="/auth">Inizia ora</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t py-8 bg-background">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src="/logo-mark.png" alt="TED Monitor" className="h-6 w-6" />
              <span className="text-sm text-muted-foreground">© {new Date().getFullYear()} TED Monitor</span>
            </div>
            <nav className="flex flex-wrap gap-6 text-sm">
              <Link to="/it/prezzi" className="text-muted-foreground hover:text-foreground">
                Prezzi
              </Link>
              <Link to="/it/monitoraggio-bandi-ue" className="text-muted-foreground hover:text-foreground">
                Monitoraggio bandi UE
              </Link>
              <Link to="/it/alert-bandi-ted" className="text-muted-foreground hover:text-foreground">
                Alert bandi
              </Link>
              <Link to="/data-source" className="text-muted-foreground hover:text-foreground">
                Fonte dati
              </Link>
              <Link to="/privacy-policy" className="text-muted-foreground hover:text-foreground">
                Privacy
              </Link>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
}
